import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Send, CheckCircle, Music, GlassWater, ConciergeBell, Package } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const prestationTypes = [
  { value: 'dj', label: 'DJ & Animation', icon: Music },
  { value: 'bar', label: 'Bar a Mocktails', icon: GlassWater },
  { value: 'serveur', label: 'Serveur a Domicile', icon: ConciergeBell },
  { value: 'complet', label: 'Prestation complete', icon: Package },
];

const eventTypes = [
  { value: 'anniversaire', label: 'Anniversaire' },
  { value: 'mariage', label: 'Mariage' },
  { value: 'fete_privee', label: 'Fete privee' },
  { value: 'seminaire', label: 'Seminaire / Team building' },
  { value: 'corporate', label: 'Evenement d\'entreprise' },
  { value: 'gala', label: 'Gala / Soiree de prestige' },
  { value: 'kermesse', label: 'Kermesse / Fete associative' },
  { value: 'autre', label: 'Autre' },
];

export default function Reservation() {
  const { user } = useAuth();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedPrestation, setSelectedPrestation] = useState('');
  const [form, setForm] = useState({
    name: '', email: '', phone: '', prestation_type: '', event_type: '',
    date: '', guests: '', location: '', message: ''
  });

  useEffect(() => {
    if (user) {
      setForm(p => ({ ...p, name: user.name || '', email: user.email || '', phone: user.phone || '' }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.prestation_type) {
      toast.error('Merci de remplir votre nom et choisir une prestation');
      return;
    }
    setSending(true);
    try {
      await api.post('/reservations', { ...form, type: 'prestation' });
      setSent(true);
      toast.success('Demande envoyee avec succes !');
    } catch {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (v) => {
    const val = typeof v === 'object' && v.target ? v.target.value : v;
    setForm(p => ({ ...p, [k]: val }));
    if (k === 'prestation_type') setSelectedPrestation(val);
  };

  if (sent) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center fade-in" data-testid="reservation-success">
        <div className="bg-[#121212] border border-[#10B981]/20 rounded-2xl p-10">
          <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Demande envoyee !</h2>
          <p className="text-sm text-[#A1A1AA] mb-2">Merci pour votre demande. Nous vous recontacterons sous 24-48h.</p>
          <p className="text-xs text-[#D4AF37] mb-6">Premier contact par SMS au 06 10 50 72 93</p>
          <Button onClick={() => { setSent(false); setForm({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '', prestation_type: '', event_type: '', date: '', guests: '', location: '', message: '' }); }}
            variant="outline" className="border-white/10 rounded-full px-6" data-testid="reservation-another">Nouvelle demande</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 fade-in" data-testid="reservation-page">
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Reservation</p>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight mb-3" style={{ fontFamily: 'Outfit' }}>
          Demandez votre <span className="text-[#D4AF37]">devis</span>
        </h1>
        <p className="text-base text-[#A1A1AA]">Parlez-nous de votre evenement et recevez une proposition personnalisee</p>
      </div>

      {/* Prestation selector */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {prestationTypes.map(p => (
          <button key={p.value} onClick={() => set('prestation_type')(p.value)} data-testid={`prestation-btn-${p.value}`}
            className={`p-4 rounded-2xl border text-center transition-all ${
              selectedPrestation === p.value
                ? 'bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#D4AF37]'
                : 'bg-[#121212] border-white/5 text-[#A1A1AA] hover:border-white/15'
            }`}>
            <p.icon className="w-6 h-6 mx-auto mb-2" />
            <span className="text-xs font-medium">{p.label}</span>
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="bg-[#121212] border border-white/5 rounded-2xl p-6 space-y-5" data-testid="reservation-form">
        <h3 className="text-sm font-semibold text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>Vos coordonnees</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Nom complet *</Label>
            <Input value={form.name} onChange={set('name')} required data-testid="reservation-name"
              placeholder="Votre nom" className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
          </div>
          <div>
            <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Email</Label>
            <Input type="email" value={form.email} onChange={set('email')} data-testid="reservation-email"
              placeholder="vous@email.com" className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Telephone</Label>
          <Input value={form.phone} onChange={set('phone')} data-testid="reservation-phone"
            placeholder="06 ..." className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
        </div>

        <div className="border-t border-white/5 pt-5">
          <h3 className="text-sm font-semibold text-[#D4AF37] mb-4" style={{ fontFamily: 'Outfit' }}>Votre evenement</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Type d'evenement</Label>
            <Select value={form.event_type} onValueChange={set('event_type')}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 rounded-xl" data-testid="reservation-event-type">
                <SelectValue placeholder="Choisir..." />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                {eventTypes.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Date souhaitee</Label>
            <Input type="date" value={form.date} onChange={set('date')} data-testid="reservation-date"
              className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Nombre d'invites</Label>
            <Input type="number" min="1" value={form.guests} onChange={set('guests')} data-testid="reservation-guests"
              placeholder="Ex: 50" className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
          </div>
          <div>
            <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Ville de l'evenement</Label>
            <Input value={form.location} onChange={set('location')} data-testid="reservation-location"
              placeholder="Ville" className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Votre message</Label>
          <Textarea value={form.message} onChange={set('message')} rows={4} data-testid="reservation-message"
            placeholder="Decrivez votre evenement : ambiance souhaitee, demandes particulieres..."
            className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl resize-none" />
        </div>

        <p className="text-[10px] text-[#A1A1AA]">
          J'accepte que mes donnees soient traitees pour repondre a ma demande.
        </p>

        <Button type="submit" disabled={sending} data-testid="reservation-submit"
          className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full h-12 font-semibold text-sm">
          {sending ? <div className="spin-gold w-5 h-5" /> : <><Send className="w-4 h-4 mr-2" /> Envoyer ma demande</>}
        </Button>
      </form>
    </div>
  );
}
